import * as React from "react"

import { cva } from "class-variance-authority"
import { ChevronDown, ChevronUp } from "lucide-react"

import { cn } from "../../cn"
import { Tooltip } from "../Tooltip/Tooltip"

/* ── CVA variants ─────────────────────────────────────────── */

const itemStepperVariants = cva(["inline-flex items-center gap-1 text-sm"])

const itemStepperButtonVariants = cva([
  "inline-flex cursor-pointer items-center justify-center",
  "h-6 w-6 rounded-md",
  "text-coval-text-muted transition-colors duration-150",
  "hover:bg-surface-default-hover hover:text-coval-text",
  "disabled:hover:bg-transparent disabled:hover:text-coval-text-muted",
  "disabled:cursor-default disabled:opacity-50",
])

/* ── Types ────────────────────────────────────────────────── */

type AccessibleName =
  | { "aria-label": string; "aria-labelledby"?: string }
  | { "aria-label"?: string; "aria-labelledby": string }

export type ItemStepperProps = React.ComponentPropsWithoutRef<"nav"> &
  AccessibleName & {
    current: number
    total: number
    onPrevious?: () => void
    onNext?: () => void
  }

/* ── Component ────────────────────────────────────────────── */

const ItemStepper = React.forwardRef<HTMLElement, ItemStepperProps>(
  ({ className, current, total, onPrevious, onNext, ...props }, ref) => (
    <nav
      ref={ref}
      role="navigation"
      className={cn(itemStepperVariants(), className)}
      {...props}
    >
      <span className="text-sm text-coval-text-muted tabular-nums">
        <span
          className="inline-block text-right"
          style={{ width: `${String(total).length}ch` }}
        >
          {current}
        </span>
        <span className="mx-0.5">/</span>
        {total}
      </span>

      <Tooltip content="Previous item" triggerDisabled={current <= 1}>
        <button
          type="button"
          aria-label="Previous item"
          disabled={current <= 1}
          onClick={onPrevious}
          className={cn(itemStepperButtonVariants())}
        >
          <ChevronUp className="size-4" />
        </button>
      </Tooltip>

      <Tooltip content="Next item" triggerDisabled={current >= total}>
        <button
          type="button"
          aria-label="Next item"
          disabled={current >= total}
          onClick={onNext}
          className={cn(itemStepperButtonVariants())}
        >
          <ChevronDown className="size-4" />
        </button>
      </Tooltip>
    </nav>
  ),
)

ItemStepper.displayName = "ItemStepper"

/* ── Exports ──────────────────────────────────────────────── */

export { ItemStepper, itemStepperVariants, itemStepperButtonVariants }
